import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { MagicWand, GoogleLogo } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError, setToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.token);
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background grain">
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-border bg-card/40 relative z-10">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
            <MagicWand size={18} weight="fill" className="text-primary-foreground" />
          </div>
          <span className="font-heading font-black text-xl tracking-tight">PostPilot</span>
        </Link>
        <div>
          <h2 className="font-heading font-black text-4xl tracking-tight leading-tight">Welcome back.</h2>
          <p className="mt-4 text-muted-foreground max-w-sm leading-relaxed">
            Pick up where you left off — your content pipeline is waiting.
          </p>
        </div>
        <p className="text-xs font-mono text-muted-foreground">© PostPilot</p>
      </div>

      <div className="flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-sm">
          <h1 className="font-heading font-bold text-2xl mb-1">Log in</h1>
          <p className="text-sm text-muted-foreground mb-6">Enter your credentials to continue.</p>

          <Button variant="outline" className="w-full mb-4" data-testid="google-login-btn" onClick={googleLogin}>
            <GoogleLogo size={18} weight="bold" className="mr-2" /> Continue with Google
          </Button>
          <div className="flex items-center gap-3 my-4">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground font-mono">OR</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="login-email" value={email}
                onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" placeholder="you@company.com" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="login-password" value={password}
                onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" placeholder="••••••••" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
              {loading ? "Logging in…" : "Log in"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-muted-foreground text-center">
            No account? <Link to="/register" className="text-primary font-medium hover:underline">Start free</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
